import { memo, useCallback, useState } from 'react'

import { hp } from 'styles/Sizes'
import { textPrimary, green } from 'styles/Colors'
import Typography from 'components/common/Typography'
import Separator from 'components/common/Separator'
import DefaultBtn from 'components/common/buttons/Default'
import TextInput from 'components/common/inputs/Text'
import LoaderView from 'components/common/loaders/LoaderView'
import Link from 'components/common/Link'
import { usePatientRouteParams } from 'components/patient/Parameters'
import { CS } from 'back-end-api'
import type { CS_m } from 'back-end-api'
import { useTranslation } from 'services/Translation'
import { ModalContainer, ModalFailed, ModalSucceeded } from '../Common'

const texts = (getText: (key: string, category: string) => string) => (key: string) =>
  ({
    succeededFeedbackLabel: getText('Feedback created', 'Patient_Details_Actions_Referral'),
    succeededReferralLabel: getText('Referral created', 'Patient_Details_Actions_Referral'),
    btnLabelClose: getText('Close', 'Patient_Details_Actions_Referral'),
    btnLabelBack: getText('Back', 'Patient_Details_Actions_Referral'),
    chooseTypeTitle: getText('Choose message type', 'Patient_Details_Actions_Referral'),
    feedbackOption: getText('Feedback (Terugkoppeling)', 'Patient_Details_Actions_Referral'),
    referralOption: getText('Medical Referral (Verwijsbrief)', 'Patient_Details_Actions_Referral'),
    feedbackSentLabel: getText('Feedback sent', 'Patient_Details_Actions_Referral'),
    referralSentLabel: getText('Referral sent', 'Patient_Details_Actions_Referral'),
    openFeedbackLabel: getText('Open feedback', 'Patient_Details_Actions_Referral'),
    openReferralLabel: getText('Open referral', 'Patient_Details_Actions_Referral'),
    btnConfirmGenerate: getText('Generate PDF', 'Patient_Details_Actions_Referral'),
    previewTitle: getText('Preview & Edit', 'Patient_Details_Actions_Referral'),
    headerLabel: getText('Header', 'Patient_Details_Actions_Referral'),
    bodyLabel: getText('Body', 'Patient_Details_Actions_Referral'),
    feedbackContentLabel: getText('Feedback', 'Patient_Details_Actions_Referral'),
    referralContentLabel: getText('Reason for referral', 'Patient_Details_Actions_Referral'),
    feedbackPlaceholder: getText('Write your feedback here...', 'Patient_Details_Actions_Referral'),
    referralPlaceholder: getText('Write your reason for referral here...', 'Patient_Details_Actions_Referral'),
    btnShowPreview: getText('Show Preview', 'Patient_Details_Actions_Referral'),
    btnHidePreview: getText('Hide Preview', 'Patient_Details_Actions_Referral'),
    btnUpdatePreview: getText('Update Preview', 'Patient_Details_Actions_Referral'),
  }[key])

enum MessageType {
  Referral = 'referral',
}

enum View {
  Menu,
  InProgress,
  Preview,
  Succeeded,
  Failed,
}

interface ReferralProps {
  onClose: () => void
}

const Referral = ({ onClose }: ReferralProps) => {
  const { t, lang } = useTranslation(texts)
  const [error, setError] = useState<string>('')
  const [, setMessageType] = useState<MessageType | null>(null)
  const { summary } = usePatientRouteParams()
  const [view, setView] = useState(View.Menu)

  // Preview state
  const [previewHtml, setPreviewHtml] = useState('')
  const [originalHtml, setOriginalHtml] = useState('')
  const [previewKey, setPreviewKey] = useState(0)
  const [header, setHeader] = useState('')
  const [body, setBody] = useState('')
  const [doctorQuery, setDoctorQuery] = useState('')
  const [previewExpanded, setPreviewExpanded] = useState(false)

  // @ts-ignore — referral_special not yet in generated ModelsSummary type
  const referralSent = !!summary?.referral_special?.url

  const openLink = useCallback((to: string) => {
    window.open(to)
  }, [])

  const backToMenu = useCallback(() => {
    setMessageType(null)
    setPreviewHtml('')
    setHeader('')
    setBody('')
    setDoctorQuery('')
    setPreviewExpanded(false)
    setView(View.Menu)
  }, [])

  const handleError = useCallback((res: { error?: { status?: number; message?: string } }) => {
    switch (res.error?.status) {
      case 400:
        setError('Internal error')
        break
      case 500:
        setError('Server error')
        break
      default:
        setError(res.error?.message ?? 'Unknown error')
    }
    setView(View.Failed)
  }, [])

  const getReferralType = useCallback((): 'standard' | 'special' => {
    return 'special'
  }, [])

  // Select type → call PreviewReferral → go to preview
  const selectType = useCallback(async (type: MessageType) => {
    setMessageType(type)
    setView(View.InProgress)

    const referralType = 'special'

    const res = await CS.previewReferral({
      id: summary?.id ?? 0,
      text: ' ',
      language_tag: lang,
      referral_type: referralType,
    })

    if (res.error) {
      handleError(res)
      return
    }

    const data = res.data as CS_m.EndpointsPreviewReferralResponse
    setPreviewHtml(data.html)
    setOriginalHtml(data.html)
    setHeader(data.data.header ?? '')
    setBody(data.data.body ?? '')
    setDoctorQuery(data.data.doctor_query ?? '')
    setView(View.Preview)
  }, [summary, lang, handleError])

  // Helper: inject doctor_query into the body text by replacing the content after the label
  const injectDoctorQuery = useCallback((bodyText: string, dq: string): string => {
    if (!dq) return bodyText
    const labels = [
      'Terugkoppeling bedrijfsarts:',
      'Feedback:',
      "'Reden verwijzing':",
      'Reason for referral:',
      'Vraagstelling MIA arts:',
      'Doctor query:',
    ]
    const matchedLabel = labels.find((label) => bodyText.includes(label))
    if (!matchedLabel) return bodyText

    const idx = bodyText.indexOf(matchedLabel)
    const afterLabel = idx + matchedLabel.length
    const rest = bodyText.substring(afterLabel)

    const greetingPatterns = ['\n\nBest Regards', '\n\nKind regards', '\n\nMet collegiale', '\n\nMet vriendelijke']
    const endIdx = greetingPatterns.reduce((minIdx, gp) => {
      const gi = rest.indexOf(gp)
      return gi !== -1 && gi < minIdx ? gi : minIdx
    }, rest.length)

    return `${bodyText.substring(0, afterLabel)} ${dq}${rest.substring(endIdx)}`
  }, [])

  // Generate final PDF with doctor_query injected into body
  const submitGenerate = useCallback(async () => {
    setView(View.InProgress)

    const fullBody = injectDoctorQuery(body, doctorQuery)

    const res = await CS.generateReferral({
      id: summary?.id ?? 0,
      referral_type: getReferralType(),
      language_tag: lang,
      data: {
        header,
        body: fullBody,
        doctor_query: doctorQuery,
      },
    })

    if (res.error) {
      handleError(res)
      return
    }

    setView(View.Succeeded)
  }, [summary, lang, header, body, doctorQuery, getReferralType, handleError, injectDoctorQuery])

  // Client-side preview update: rebuild preview HTML from edited fields
  const updatePreview = useCallback(() => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(originalHtml, 'text/html')
    const headHtml = doc.querySelector('head')?.innerHTML ?? ''

    // Inject doctor query into body text
    const fullBody = injectDoctorQuery(body, doctorQuery)

    const escapedBody = fullBody.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    const escapedHeader = header.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

    const newHtml = `<!DOCTYPE html><html><head>${headHtml}</head><body style="font-family: Arial, sans-serif; font-size: 11pt; line-height: 1.5; color: #333; padding: 10px;"><div style="font-size: 22pt; font-weight: bold; color: #0066cc; border-bottom: 2px solid #0066cc; padding-bottom: 15px; margin-bottom: 20px;">${escapedHeader}</div><pre style="font-family: inherit; white-space: pre-wrap; margin: 0; line-height: 1.5;">${escapedBody}</pre></body></html>`

    setPreviewHtml(newHtml)
    setPreviewKey(prev => prev + 1)
    setPreviewExpanded(true)
  }, [originalHtml, header, body, doctorQuery, injectDoctorQuery])

  switch (view) {
    case View.InProgress:
      return <LoaderView onClose={onClose} />
    case View.Failed:
      return (
        <ModalFailed errorMsg={error} containerProps={{ label: t('btnLabelClose'), onClose: backToMenu }} />
      )
    case View.Succeeded:
      return (
        <ModalSucceeded
          label={t('succeededReferralLabel')}
          containerProps={{ label: t('btnLabelBack'), onClose: backToMenu }}
        />
      )
    case View.Menu:
      return (
        <ModalContainer onClose={onClose} label={t('btnLabelClose')}>
          <Typography type="title" className="font-csc65" text={t('chooseTypeTitle')} color={textPrimary} size={18} style={undefined} />
          <Separator size={hp(4)} />
          <div className="flex flex-col w-full">
            {referralSent ? (
              <div className="flex items-center justify-between w-full py-8 px-4 border border-grey-100 rounded">
                <Typography type="body" className="font-csc65" text={t('referralSentLabel')} color={textPrimary} size={14} style={undefined} />
                {/* @ts-ignore — referral_special not yet in generated ModelsSummary type */}
                <Link underlineOnHover to={summary?.referral_special?.url ?? ''} onClick={() => openLink(summary?.referral_special?.url ?? '')} target="_blank" label={t('openReferralLabel')} />
              </div>
            ) : (
              <DefaultBtn label={t('referralOption')} onClick={() => selectType(MessageType.Referral)} className="font-csc65" reversedColors color={green} style={{ width: '100%', padding: '12px 16px' }} nextIcon={undefined} disabled={false} />
            )}
          </div>
        </ModalContainer>
      )
    case View.Preview:
      return (
        <ModalContainer onClose={backToMenu} label={t('btnLabelBack')}>
          <Typography type="title" className="font-csc65" text={t('previewTitle')} color={textPrimary} size={18} style={undefined} />
          <Separator size={hp(2)} />

          {/* Collapsible HTML Preview */}
          <div className="flex w-full gap-2" style={{ marginBottom: 8 }}>
            <DefaultBtn
              label={previewExpanded ? t('btnHidePreview') : t('btnShowPreview')}
              onClick={() => setPreviewExpanded(prev => !prev)}
              className="font-csc65"
              reversedColors
              color={textPrimary}
              style={{ padding: '6px 12px', fontSize: 12 }}
              nextIcon={undefined}
              disabled={false}
            />
            <DefaultBtn
              label={t('btnUpdatePreview')}
              onClick={updatePreview}
              className="font-csc65"
              reversedColors
              color={textPrimary}
              style={{ padding: '6px 12px', fontSize: 12 }}
              nextIcon={undefined}
              disabled={false}
            />
          </div>

          {previewExpanded && (
            <iframe
              key={previewKey}
              srcDoc={previewHtml}
              sandbox="allow-same-origin"
              title="Referral Preview"
              style={{ width: '100%', height: '400px', border: '1px solid #e5e7eb', borderRadius: '4px', marginBottom: 8 }}
            />
          )}

          {/* Editable header + body + doctor query */}
          <div className="flex flex-col w-full gap-3">
            <div>
              <Typography type="body" className="font-csc65" text={t('headerLabel')} color={textPrimary} size={12} style={{ marginBottom: 4 }} />
              <TextInput
                field={{ onChange: ({ target: { value } }: any) => setHeader(value), value: header }}
                noBorder noPadding
                style={{ border: '1px solid #e5e7eb', borderRadius: 4, padding: '6px 8px' }}
                label={undefined} inputRef={undefined} inputStyle={undefined} containerStyle={undefined} placeholderStyle={undefined}
              />
            </div>
            <div>
              <Typography type="body" className="font-csc65" text={t('bodyLabel')} color={textPrimary} size={12} style={{ marginBottom: 4 }} />
              <TextInput
                field={{ onChange: ({ target: { value } }: any) => setBody(value), value: body }}
                noBorder multiline noPadding
                style={{ border: '1px solid #e5e7eb', borderRadius: 4, padding: '6px 8px', minHeight: 100 }}
                label={undefined} inputRef={undefined} inputStyle={undefined} containerStyle={undefined} placeholderStyle={undefined}
              />
            </div>
            <div>
              <Typography
                type="body"
                className="font-csc65"
                text={t('referralContentLabel')}
                color={textPrimary}
                size={12}
                style={{ marginBottom: 4 }}
              />
              <TextInput
                field={{ onChange: ({ target: { value } }: any) => setDoctorQuery(value), value: doctorQuery }}
                noBorder multiline noPadding
                placeholder={t('referralPlaceholder')}
                style={{ border: '1px solid #e5e7eb', borderRadius: 4, padding: '6px 8px', minHeight: 80 }}
                label={undefined} inputRef={undefined} inputStyle={undefined} containerStyle={undefined} placeholderStyle={undefined}
              />
            </div>
          </div>

          <Separator size={hp(3)} />
          <div className="w-full flex justify-center align-center border-t border-grey-100 pt-12">
            <DefaultBtn
              label={t('btnConfirmGenerate')}
              onClick={submitGenerate}
              type="submit"
              className="font-csc65"
              reversedColors
              color={green}
              style={{ width: '9.375rem' }}
              data-cy="btnGenerate"
              nextIcon={undefined}
              disabled={false}
            />
          </div>
        </ModalContainer>
      )
    default:
      console.error('Unhandled view: ', view)
      return null
  }
}

Referral.whyDidYouRender = true

export default memo(Referral)
